import { useCallback, useRef, useState, type CSSProperties } from 'react';
import { useClosingAnimationEnd } from '@hooks/useClosingAnimationEnd';
import { ORCA_PATH, ORCA_VIEWBOX, SUN } from './orcaGeometry';
import { RUN_MS, SPRAY, flightMs } from './splash';
import styles from './OrcaLogo.module.css';

interface IOrcaLogoProps {
  className?: string;
  animated?: boolean; // Pointer-enter plays the breach once, through to the end
}

/**
 * The D-ORCA mark — the orca leaping in front of the rising sun — drawn inline
 * so its parts can move. With `animated`, entering it plays a breach: the orca
 * completes its leap, dives through the bottom edge and bursts back in, with
 * spray where it cuts the water; the sun holds still. The run always finishes,
 * so a quick pass of the pointer never cuts it off mid-air.
 */
export function OrcaLogo({ className, animated = false }: IOrcaLogoProps) {
  const [playing, setPlaying] = useState(false);
  const orcaRef = useRef<SVGGElement>(null);
  const finish = useCallback(() => setPlaying(false), []);
  // The orca's layers run the full length of the scene, so the end of the
  // outer one closes the run — the same finish signal the drawers use, which
  // also ends it at once when nothing is running (reduced motion).
  useClosingAnimationEnd(orcaRef, playing, finish);

  return (
    <svg
      className={[styles.root, className].filter(Boolean).join(' ')}
      viewBox={`0 0 ${ORCA_VIEWBOX} ${ORCA_VIEWBOX}`}
      data-playing={playing}
      style={{ '--run': `${RUN_MS}ms` } as CSSProperties}
      onMouseEnter={animated ? () => setPlaying(true) : undefined}
      aria-hidden
    >
      <circle className={styles.sun} cx={SUN.cx} cy={SUN.cy} r={SUN.r} />
      {/* Spray sits behind the orca so it reads as thrown up around the body. */}
      {SPRAY.map((d) => {
        const timing = { animationDuration: `${flightMs(d.rise)}ms`, animationDelay: `${d.at}ms` };
        return (
          <g
            key={`${d.x}-${d.at}`}
            className={styles.dropX}
            style={{ '--drift': `${d.drift}px`, ...timing } as CSSProperties}
          >
            {/* Starts 1.5r under the surface: its launch stretch is 1.5× tall. */}
            <circle
              className={styles.drop}
              cx={d.x}
              cy={ORCA_VIEWBOX + 1.5 * d.r}
              r={d.r}
              style={{ '--rise': `${-d.rise}px`, ...timing } as CSSProperties}
            />
          </g>
        );
      })}
      <g ref={orcaRef} className={styles.orcaX}>
        <g className={styles.orcaY}>
          <g className={styles.orca}>
            <path fillRule="evenodd" d={ORCA_PATH} />
          </g>
        </g>
      </g>
    </svg>
  );
}
